import axios from 'axios'
import { isQrPasswordValid, normalizeQrPassword } from 'features/shared/client'

import { getStore } from '@/store'

import { qrTypeAtom } from '../../generatorState'
import { QR_TYPE } from '../../generatorTypes'
import { qrCodesApi } from '../../services/shared'
import {
	passwordAtom,
	passwordEnabledAtom,
	passwordLoadedForTargetAtom,
	passwordRepeatAtom,
	savedPasswordAtom,
} from './passwordDesignState'

const isPasswordFormApplicable = () =>
	getStore().get(qrTypeAtom) !== QR_TYPE.Wifi &&
	getStore().get(passwordEnabledAtom)

export const isQrPasswordBlockingFinish = () =>
	isPasswordFormApplicable() &&
	!isQrPasswordValid(
		getStore().get(passwordAtom),
		getStore().get(passwordRepeatAtom),
	)

export const getDesiredQrPassword = (): string | null =>
	isPasswordFormApplicable()
		? normalizeQrPassword(getStore().get(passwordAtom))
		: null

export const loadSavedQrPassword = async (qrId: string) => {
	try {
		const { data } = await qrCodesApi.getQrPassword(qrId)
		return data.password
	} catch (error) {
		if (axios.isAxiosError(error) && error.response?.status === 404) {
			return null
		}
		throw error
	}
}

export const writeQrPassword = (
	qrId: string,
	password: string | null,
	isAuthenticated: boolean,
) => {
	if (password === null) {
		return isAuthenticated
			? qrCodesApi.deleteQrPassword(qrId)
			: qrCodesApi.deleteGuestQrPassword(qrId)
	}
	return isAuthenticated
		? qrCodesApi.putQrPassword(qrId, password)
		: qrCodesApi.putGuestQrPassword(qrId, password)
}

const persistQrPassword = async ({
	qrId,
	isAuthenticated,
	desired,
	saved,
	isSavedKnown,
}: {
	qrId: string
	isAuthenticated: boolean
	desired: string | null
	saved: string | null
	isSavedKnown: boolean
}): Promise<string | null> => {
	if (desired !== null) {
		if (desired !== saved || !isAuthenticated) {
			await writeQrPassword(qrId, desired, isAuthenticated)
		}
		return desired
	}
	if (saved !== null || !isSavedKnown) {
		await writeQrPassword(qrId, null, isAuthenticated)
	}
	return null
}

// undefined leaves the live password as it is, null removes it
export const getQrPasswordPublishValue = (): string | null | undefined => {
	const desired = getDesiredQrPassword()
	return desired === getStore().get(savedPasswordAtom) ? undefined : desired
}

export const syncBuilderQrPassword = async ({
	qrId,
	isAuthenticated,
}: {
	qrId: string
	isAuthenticated: boolean
}) => {
	const saved = await persistQrPassword({
		qrId,
		isAuthenticated,
		desired: getDesiredQrPassword(),
		saved: getStore().get(savedPasswordAtom),
		// Guests can't read the password back, so after a reload the draft may
		// hold one this session never saw
		isSavedKnown: getStore().get(passwordLoadedForTargetAtom) === qrId,
	})
	getStore().set(savedPasswordAtom, saved)
	getStore().set(passwordLoadedForTargetAtom, qrId)
}
